import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { AdminSection } from "./admin";
import { AdminButton, AdminDialog, Field, Input, Row, StatusBadge, TableShell, Textarea } from "@/components/admin/AdminUI";
import { MediaUpload } from "@/components/admin/MediaUpload";

export const Route = createFileRoute("/_authenticated/admin/library")({
  head: () => ({ meta: [{ title: "Library · Admin" }, { name: "robots", content: "noindex" }] }),
  component: LibraryAdmin,
});

type T = Database["public"]["Tables"]["digital_titles"]["Row"];

function LibraryAdmin() {
  const [rows, setRows] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<T | null>(null);

  async function load() {
    setLoading(true);
    const { data, error } = await supabase
      .from("digital_titles")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) console.error(error);
    setRows(data ?? []);
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  return (
    <AdminSection
      title="Knowledge Library"
      description="Manage covers and metadata for digital titles (books, courses, activations)."
    >
      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">No titles yet.</p>
      ) : (
        <TableShell
          headers={["Cover", "Title", "Kind", "Status", ""]}
          rows={rows.map((r) => (
            <tr key={r.id} className="hover:bg-obsidian/40">
              <td className="px-4 py-3">
                {r.cover_url ? (
                  <img src={r.cover_url} alt="" className="h-16 w-12 rounded object-cover border hairline" />
                ) : (
                  <div className="h-16 w-12 rounded border border-dashed hairline text-[10px] flex items-center justify-center text-muted-foreground">
                    None
                  </div>
                )}
              </td>
              <td className="px-4 py-3">
                <div className="font-serif text-base text-gold-luminous">{r.title}</div>
                <div className="text-xs text-muted-foreground">/{r.slug}</div>
              </td>
              <td className="px-4 py-3 text-xs uppercase tracking-[0.2em] text-muted-foreground">{r.kind}</td>
              <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
              <td className="px-4 py-3">
                <div className="flex justify-end">
                  <AdminButton variant="ghost" onClick={() => setEditing(r)}>Edit</AdminButton>
                </div>
              </td>
            </tr>
          ))}
        />
      )}
      {editing && (
        <EditForm
          row={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </AdminSection>
  );
}

function EditForm({ row, onClose, onSaved }: { row: T; onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState({
    title: row.title,
    subtitle: row.subtitle ?? "",
    author: row.author ?? "",
    cover_url: row.cover_url ?? "",
    description: row.description ?? "",
    status: row.status,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    const { error } = await supabase
      .from("digital_titles")
      .update({
        title: f.title.trim(),
        subtitle: f.subtitle.trim() || null,
        author: f.author.trim() || null,
        cover_url: f.cover_url.trim() || null,
        description: f.description.trim() || null,
        status: f.status,
      })
      .eq("id", row.id);
    setBusy(false);
    if (error) setError(error.message); else onSaved();
  }

  return (
    <AdminDialog open onClose={onClose} title={`Edit · ${row.title}`}>
      <form onSubmit={submit} className="space-y-4">
        <Field label="Cover image" hint="Recommended 800×1200 (portrait). PNG or JPG.">
          <MediaUpload
            value={f.cover_url}
            onChange={(url) => setF({ ...f, cover_url: url })}
            folder={`titles/${row.slug}`}
          />
        </Field>
        <Row>
          <Field label="Title"><Input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></Field>
          <Field label="Author"><Input value={f.author} onChange={(e) => setF({ ...f, author: e.target.value })} /></Field>
        </Row>
        <Field label="Subtitle"><Input value={f.subtitle} onChange={(e) => setF({ ...f, subtitle: e.target.value })} /></Field>
        <Field label="Description"><Textarea rows={4} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field>
        <Field label="Status">
          <select
            value={f.status}
            onChange={(e) => setF({ ...f, status: e.target.value })}
            className="w-full rounded-md border hairline bg-transparent px-3 py-2 text-sm outline-none focus:border-gold"
          >
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </select>
        </Field>
        {error && <p className="text-sm text-red-400">{error}</p>}
        <div className="flex justify-end gap-2 border-t hairline pt-4">
          <AdminButton type="button" variant="ghost" onClick={onClose}>Cancel</AdminButton>
          <AdminButton type="submit" disabled={busy}>{busy ? "Saving…" : "Save"}</AdminButton>
        </div>
      </form>
    </AdminDialog>
  );
}
